<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready" />
        <b-form @submit.prevent="onSubmit">
          <b-card header="Activity Event Details" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner />
              </slot>
            </Loading>
            <hm-alert />

            <b-form-group label="When did the system activity review take place?" label-class="font-weight-bold">
              <vue-datepicker
                name="date"
                placeholder="YYYY-MM-DD"
                format="yyyy-MM-dd"
                v-model="activity.date"
                bootstrap-styling
                clear-button
                clear-button-icon="fa fa-times-circle"
                v-validate="'required'"
                :disabled="busy"
                :input-class="{ 'bg-white': true, 'is-invalid': errors.has('date') && submitted }"
              />
            </b-form-group>
            <b-form-group>
              <div slot="label">
                <strong>Who performed the system activity review (&quot;reviewer&quot;)?</strong>
                <br />This person shall have appropriate technical skills with respect to the operating system and applications to access and interpret audit logs and related information.
              </div>
              <b-form-select
                name="reviewer"
                class="form-control"
                :options="workforceNames"
                v-model="activity.reviewer"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('reviewer') && submitted }"
              >
                <template slot="first">
                  <option :value="''" disabled>-- Please select an option --</option>
                </template>
              </b-form-select>
            </b-form-group>
            <b-form-group>
              <div slot="label">
                <strong>
                  Were any security-significant events related to
                  <u>logins</u> discovered?
                </strong>
                <br />Scan successful and unsuccessful login attempts. Identify multiple failed login attempts, account lockouts, and unauthorized access.
              </div>
              <div v-for="(value, key) in activityMap.Logins" :key="key">
                <hm-switch
                  name="logins"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="activity.logins"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('logins') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group>
              <div slot="label">
                <strong>
                  Were any security-significant events related to
                  <u>file access</u> discovered?
                </strong>
                <br />Scan successful and unsuccessful file access attempts. Identify multiple failed access attempts, unauthorized access, and unauthorized file creation, modification, or deletion.
              </div>
              <div v-for="(value, key) in activityMap.FileAccess" :key="key">
                <hm-switch
                  name="fileAccess"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="activity.fileAccess"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('fileAccess') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group>
              <div slot="label">
                <strong>
                  Were any security-significant events related to
                  <u>security incidents</u> discovered?
                </strong>
                <br />Examine records from security devices or system audit logs for events that constitute system compromises, unsuccessful compromise attempts, malicious logic (e.g., viruses, worms), denial of service, or scanning/probing incidents. For example, scan computers
                using anti-virus software.
              </div>
              <div v-for="(value, key) in activityMap.Incidents" :key="key">
                <hm-switch
                  name="incidents"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="activity.incidents"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('incidents') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group>
              <div slot="label">
                <strong>
                  Were any security-significant events related to
                  <u>user accounts</u> discovered?
                </strong>
                <br />Review user accounts within all systems to ensure users who no longer have a business need for information systems no longer have access, and make sure valid users access information appropriately, e.g., they aren't accessing records they shouldn't be.
              </div>
              <div v-for="(value, key) in activityMap.Accounts" :key="key">
                <hm-switch
                  name="accounts"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="activity.accounts"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('accounts') && submitted }"
                />
              </div>
              <hm-note>
                <u>Note</u>: Every EHR/PM software system should have audit log capabilities to help perform this task. Contact your software vendor and ask for assistance if you do not know how to operate their audit log feature.
              </hm-note>
            </b-form-group>
            <b-form-group label="What were the findings of the event(s) discovered above?" label-class="font-weight-bold">
              <b-form-textarea
                no-resize
                :rows="5"
                name="findings"
                class="form-control"
                placeholder="Describe the security incident(s) in as much detail as possible."
                v-model="activity.findings"
                v-validate="findingsRequiredValidator"
                :disabled="busy || !findingsRequired"
                :class="{ 'is-invalid': errors.has('findings') || (findingsRequired && activity.findings.length == 0) }"
              />
              <hm-note v-if="findingsRequired">
                <u>Important</u>: There is more work to be done! Be sure to print the
                <a :href="securityIncidentUrl" target="_blank">Security Incident Response Form</a>
                and complete required steps.
              </hm-note>
            </b-form-group>

            <div slot="footer">
              <b-row>
                <b-col cols="6">
                  <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/activity-list">Back</b-button>
                  <b-button type="button" variant="danger" class="px-4 ml-2" :disabled="busy" v-show="id" @click="dlgDelete = true">Delete</b-button>
                </b-col>
                <b-col cols="6" class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Save</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>

    <!-- Additional Work Modal Dialog -->
    <b-modal id="dlgSecurityEvent" class="modal-primary" hide-header-close v-model="dlgSecurityEvent">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-exclamation-triangle pr-2"></i>Additional Work Required
      </div>
      <p>
        Because you responded
        <strong>Yes</strong> for at least one question,
        completing the Security Incident Response Form is very important.
        Please remember to print the document and complete the process in a timely manner.
        Use the Support button if you have questions.
      </p>
      <a :href="securityIncidentUrl" target="_blank">Security Incident Response Form</a>
      <div slot="modal-footer">
        <b-button variant="primary" class="ml-2" @click="dlgSecurityEvent = false; onSave()">OK</b-button>
      </div>
    </b-modal>

    <!-- Delete Modal Dialog -->
    <b-modal id="dlgDelete" class="modal-primary" hide-header-close v-model="dlgDelete">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-trash pr-2"></i>Delete Confirmation
      </div>
      <p>Are you sure you want to delete this activity review session? All data will be gone forever and no report generated.</p>
      <div slot="modal-footer">
        <b-button variant="default" @click="dlgDelete = false">No</b-button>
        <b-button variant="primary" class="ml-2" @click="dlgDelete = false; onDelete()">Yes</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { practiceService, workforceService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'ActivityEdit',
  props: ['id'],
  data() {
    return {
      activity: {
        id: '',
        date: new Date((new Date()).setHours(0, 0, 0, 0)), // today
        reviewer: '',
        logins: '',
        fileAccess: '',
        incidents: '',
        accounts: '',
        findings: ''
      },
      securityIncidentUrl: 'download/Security Incident Response Form.pdf',
      workforce: [],
      workforceNames: [],
      dlgSecurityEvent: false,
      dlgDelete: false,
      ready: false,
      busy: false,
      submitted: false
    }
  },
  computed: {
    ...mapState('maps', ['activityMap', 'workforceMap']),
    findingsRequired() {
      return (this.activity.logins == 'yes' || this.activity.fileAccess == 'yes' || this.activity.incidents == 'yes' || this.activity.accounts == 'yes');
    },
    findingsRequiredValidator() {
      return (this.findingsRequired) ? 'required' : null;
    },
    activeWorkforce() {
      return Enumerable.from(this.workforce).where(x => { return x.active; }).toArray();
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertError: 'alert/error',
      alertException: 'alert/exception',
      setProgress: 'progress/setFlagDone'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            if (this.findingsRequired) {
              this.dlgSecurityEvent = true;
            } else {
              return this.onSave();
            }
          } else {
            this.alertError('Please complete activity information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onSave() {
      this.busy = true;

      return practiceService.setActivity(this.activity)
        .then(data => {
          this.setProgress('ActivitySet');
          this.$router.push('/activity-list');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving activity.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onDelete() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      return practiceService.deleteActivity(this.activity)
        .then(data => {
          this.$router.push('/activity-list');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error deleting activity.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchActivityMap'),
        workforceService.getWorkforce()
      ]).then((values) => {
        let workforce = values[1];

        this.workforce = (workforce && workforce.length > 0) ? workforce : [];
        this.workforceNames = Enumerable.from(workforce).where(x => { return x.active; }).select(x => { return x.name; }).toArray();

        if (this.id) {
          return practiceService.getActivity(this.id)
            .then(data => {
              data.date = new Date(data.date);

              if (this.workforceNames.indexOf(data.reviewer) < 0) {
                this.workforceNames.push(data.reviewer);
              }
              // set any defaults that are missing from data to the default values
              this.activity = { ...this.$options.data.apply(this).activity, ...data };
            })
            .catch(err => {
              this.alertException({ err, message: 'Error loading activity item.' });
            });
        } else {
          return this.activity;
        }
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading data.' });
      }).finally(() => {
        this.ready = true;
      })
    }
  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>